
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ImageDropzone } from "../ImageDropzone";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import type { BlogPost } from "../types";
import { toast } from "sonner";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface BlogPostFormProps {
  formData: Partial<BlogPost>;
  onFormDataChange: (data: Partial<BlogPost>) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isEditing: boolean;
  existingCategories: string[];
}

export const BlogPostForm = ({
  formData,
  onFormDataChange,
  onSubmit,
  onCancel,
  isEditing,
  existingCategories
}: BlogPostFormProps) => {
  const [newCategory, setNewCategory] = useState("");

  const handleAddCategory = () => {
    if (!newCategory.trim()) return;
    if (formData.categories?.includes(newCategory.trim())) {
      toast.error("Category already exists");
      return;
    }
    onFormDataChange({
      ...formData,
      categories: [...(formData.categories || []), newCategory.trim()]
    });
    setNewCategory("");
    toast.success("Category added");
  };

  const removeCategory = (categoryToRemove: string) => {
    onFormDataChange({
      ...formData,
      categories: formData.categories?.filter(cat => cat !== categoryToRemove) || []
    });
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      ['link', 'image'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet',
    'link', 'image'
  ];

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>
          {isEditing ? "Edit Post" : "Create New Post"}
        </DialogTitle>
      </DialogHeader>
      
      <div className="space-y-4">
        <div>
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            value={formData.title || ""}
            onChange={(e) => onFormDataChange({ ...formData, title: e.target.value })}
          />
        </div>

        <div>
          <Label>Content</Label>
          <div className="min-h-[200px] border rounded-md bg-background">
            <ReactQuill
              theme="snow"
              value={formData.content || ""}
              onChange={(content) => onFormDataChange({ ...formData, content })}
              modules={modules}
              formats={formats}
              className="h-[300px] mb-12"
            />
          </div>
        </div>

        <div>
          <Label>Featured Image</Label>
          <ImageDropzone
            value={formData.featuredImage || ""}
            onChange={(base64) => onFormDataChange({ ...formData, featuredImage: base64 })}
          />
        </div>

        <div>
          <Label htmlFor="tags">Tags (comma-separated)</Label>
          <Input
            id="tags"
            value={formData.tags?.join(", ") || ""}
            onChange={(e) => onFormDataChange({
              ...formData,
              tags: e.target.value.split(",").map(tag => tag.trim()).filter(Boolean)
            })}
          />
        </div>

        <div className="space-y-2">
          <Label>Categories</Label>
          <div className="flex gap-2 flex-wrap mb-3">
            {formData.categories?.map((category) => (
              <Badge key={category} variant="secondary" className="group">
                {category}
                <button
                  type="button"
                  onClick={() => removeCategory(category)}
                  className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
          {existingCategories.length > 0 && (
            <div className="mb-3">
              <Label className="text-sm text-muted-foreground">Existing Categories</Label>
              <div className="flex gap-2 flex-wrap mt-1">
                {existingCategories
                  .filter(cat => !formData.categories?.includes(cat))
                  .map((category) => (
                    <Badge
                      key={category}
                      variant="outline"
                      className="cursor-pointer hover:bg-secondary"
                      onClick={() => onFormDataChange({
                        ...formData,
                        categories: [...(formData.categories || []), category]
                      })}
                    >
                      {category}
                    </Badge>
                  ))}
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Input
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Add new category"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCategory();
                }
              }}
            />
            <Button type="button" onClick={handleAddCategory}>Add</Button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id="published"
            checked={formData.isPublished || false}
            onCheckedChange={(checked) => onFormDataChange({ ...formData, isPublished: checked })}
          />
          <Label htmlFor="published">Published</Label>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onSubmit}>
          {isEditing ? "Update" : "Create"} Post
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
